<template>
  <!-- Add/Edit Client (UPGRADED UI ONLY) -->
  <div class="modal fade dq-client-modal" id="add_client" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-xl dq-client-dialog">
      <div class="modal-content dq-modal-content">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-building-store"></i>
            </div>
            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">
                {{ isEdit ? "Edit Client" : "Add New Client" }}
              </h4>
              <div class="dq-subtitle">
                Manage client details, logo and portal permissions.
              </div>
            </div>
          </div>

          <div class="dq-codewrap">
            <span class="dq-chip">
              <i class="ti ti-barcode"></i>
              <span class="dq-chip-label">Client Code</span>
              <span class="dq-chip-value">{{ displayClientCode }}</span>
            </span>

            <button
              type="button"
              class="btn-close custom-btn-close dq-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i class="ti ti-x"></i>
            </button>
          </div>
        </div>

        <form ref="formEl" @submit.prevent="submit">
          <!-- Tabs -->
          <div class="dq-tabs">
            <ul class="nav nav-underline dq-nav">
              <li class="nav-item">
                <button
                  type="button"
                  class="nav-link active"
                  data-bs-toggle="tab"
                  data-bs-target="#basic-info"
                >
                  <i class="ti ti-id"></i>
                  Basic Information
                </button>
              </li>
              <li class="nav-item">
                <button
                  type="button"
                  class="nav-link"
                  :class="{
                    'dq-nav-link-disabled': !canConfigurePortalPermissions,
                  }"
                  data-bs-toggle="tab"
                  data-bs-target="#permissions"
                  :disabled="!canConfigurePortalPermissions"
                  :aria-disabled="!canConfigurePortalPermissions"
                >
                  <i class="ti ti-shield-lock"></i>
                  Permissions
                  <span
                    v-if="!canConfigurePortalPermissions"
                    class="dq-tab-lock"
                  >
                    Locked
                  </span>
                </button>
              </li>
            </ul>
          </div>

          <div class="tab-content">
            <!-- Basic -->
            <div id="basic-info" class="tab-pane fade show active">
              <div class="modal-body dq-modal-body">
                <!-- Logo row -->
                <div class="dq-logo-card">
                  <div class="dq-logo-left">
                    <div class="dq-avatar">
                      <img
                        v-if="preview"
                        :src="preview"
                        alt="Client logo preview"
                      />
                      <i v-else class="ti ti-photo"></i>
                    </div>

                    <div class="dq-logo-meta">
                      <div class="dq-logo-title">Upload Logo</div>
                      <div class="dq-logo-sub">
                        PNG/JPG recommended • max 4MB
                      </div>

                      <div class="dq-logo-actions">
                        <label class="btn btn-primary btn-sm dq-btn-file">
                          <i class="ti ti-upload me-1"></i> Upload
                          <input
                            type="file"
                            accept="image/*"
                            class="dq-hidden-file"
                            @change="onFileChange"
                          />
                        </label>

                        <button
                          v-if="preview"
                          type="button"
                          class="btn btn-light btn-sm dq-btn-muted"
                          @click="clearFile"
                        >
                          <i class="ti ti-x me-1"></i> Remove
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="dq-logo-right">
                    <div class="dq-tip">
                      <i class="ti ti-info-circle"></i>
                      <span>
                        Tip: Use a square logo for the best appearance in CRM
                        cards and exports.
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Form grid -->
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label dq-label">
                      Client/Company Name <span class="text-danger">*</span>
                    </label>
                    <input
                      v-model.trim="form.name"
                      type="text"
                      class="form-control dq-input"
                      placeholder="e.g. Example Technologies"
                      required
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Client Code</label>
                    <input
                      v-model.trim="form.client_code"
                      type="text"
                      class="form-control dq-input"
                      readonly
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Email</label>
                    <input
                      v-model.trim="form.email"
                      type="email"
                      class="form-control dq-input"
                      placeholder="e.g. finance@client.com"
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Phone</label>
                    <input
                      v-model.trim="form.phone"
                      type="text"
                      class="form-control dq-input"
                      placeholder="e.g. +2547..."
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Website</label>
                    <input
                      v-model.trim="form.website"
                      type="text"
                      class="form-control dq-input"
                      placeholder="e.g. https://client.com"
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Status</label>
                    <vue-select
                      v-model="form.status"
                      :options="['Active', 'Inactive']"
                      placeholder="Select Status"
                      class="dq-vselect"
                    />
                  </div>

                  <div class="col-md-12">
                    <label class="form-label dq-label">Address</label>
                    <textarea
                      v-model.trim="form.address"
                      class="form-control dq-input dq-textarea"
                      rows="2"
                      placeholder="Street, building, city..."
                    />
                  </div>

                  <!-- Portal user -->
                  <div class="col-12">
                    <div class="dq-switch-row">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">
                          Client Portal Access
                        </div>
                        <div class="dq-switch-sub">
                          Allow this client to log in to the portal (role:
                          Client).
                        </div>
                      </div>

                      <div class="form-check form-switch m-0">
                        <input
                          id="create_user"
                          class="form-check-input"
                          type="checkbox"
                          v-model="form.create_portal_user"
                          :disabled="isEdit && hadPortalUser"
                        />
                        <label class="form-check-label" for="create_user">
                          {{
                            isEdit && hadPortalUser
                              ? "Already created"
                              : form.create_portal_user
                              ? "Enabled"
                              : "Disabled"
                          }}
                        </label>
                      </div>
                    </div>

                    <div
                      v-if="isEdit && hadPortalUser"
                      class="dq-note mt-2"
                    >
                      <i class="ti ti-lock"></i>
                      Portal user already exists for this client.
                    </div>
                    <div
                      v-else-if="form.create_portal_user"
                      class="dq-note dq-note-success mt-2"
                    >
                      <i class="ti ti-circle-check"></i>
                      Portal access is enabled. You can now configure client
                      portal permissions.
                    </div>
                    <div
                      v-else
                      class="dq-note dq-note-muted mt-2"
                    >
                      <i class="ti ti-info-circle"></i>
                      Enable Client Portal Access first to unlock permissions.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="modal-footer dq-modal-footer">
                <button
                  type="button"
                  class="btn btn-light dq-btn dq-btn-muted"
                  data-bs-dismiss="modal"
                  :disabled="store.loading"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  class="btn btn-primary dq-btn dq-btn-primary"
                  :disabled="store.loading"
                >
                  <span
                    v-if="store.loading"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  {{ isEdit ? "Save Changes" : "Save Client" }}
                  <i class="ti ti-arrow-right ms-2"></i>
                </button>
              </div>
            </div>

            <!-- Permissions -->
            <div id="permissions" class="tab-pane fade">
              <div class="modal-body dq-modal-body">
                <div
                  v-if="!canConfigurePortalPermissions"
                  class="dq-perm-locked"
                >
                  <div class="dq-perm-locked-icon">
                    <i class="ti ti-lock"></i>
                  </div>
                  <div class="dq-perm-locked-title">
                    Permissions are available only for portal-enabled clients
                  </div>
                  <div class="dq-perm-locked-sub">
                    Turn on <b>Client Portal Access</b> in Basic Information to
                    create a client portal user and configure module
                    permissions.
                  </div>
                </div>

                <div class="dq-perm-head">
                  <div>
                    <div class="dq-perm-title">Enable Options</div>
                    <div class="dq-perm-sub">
                      Turn on modules and select allowed actions.
                    </div>
                  </div>

                  <div class="dq-perm-controls">
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :checked="allOptionalModulesEnabled"
                        @change="setAllModules($event.target.checked)"
                        id="perm_enable_all"
                        :disabled="!canConfigurePortalPermissions"
                      />
                      <label class="form-check-label" for="perm_enable_all">
                        Enable all modules
                      </label>
                    </div>

                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :checked="allEnabledActionsSelected"
                        @change="setAllEnabledActions($event.target.checked)"
                        id="perm_select_all"
                        :disabled="
                          !canConfigurePortalPermissions || !hasEnabledOptionalModules
                        "
                      />
                      <label class="form-check-label" for="perm_select_all">
                        Select all actions
                      </label>
                    </div>
                  </div>
                </div>

                <div class="dq-tablewrap">
                  <div class="table-responsive">
                    <table class="table dq-table mb-0">
                      <tbody>
                        <tr v-for="perm in permissionsList" :key="perm.module">
                          <td class="dq-module-cell">
                            <div class="form-check form-switch m-0">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                :checked="perm.enabled"
                                @change="toggleModule(perm, $event.target.checked)"
                                :id="`mod_${perm.module}`"
                                :disabled="
                                  !canConfigurePortalPermissions || isRequiredModule(perm)
                                "
                              />
                              <label
                                class="form-check-label"
                                :for="`mod_${perm.module}`"
                              >
                                <span class="dq-module-name">{{ perm.module }}</span>
                                <span
                                  v-if="isRequiredModule(perm)"
                                  class="dq-required-chip"
                                >
                                  Required
                                </span>
                              </label>
                            </div>
                          </td>

                          <td
                            v-for="action in perm.actions"
                            :key="action.name"
                            class="dq-action-cell"
                          >
                            <label
                              class="dq-check"
                              :for="`perm_${perm.permissionKey}_${action.name}`"
                            >
                              <input
                                :id="`perm_${perm.permissionKey}_${action.name}`"
                                class="form-check-input"
                                type="checkbox"
                                :checked="action.value"
                                @change="toggleAction(perm, action, $event.target.checked)"
                                :disabled="
                                  !canConfigurePortalPermissions || !perm.enabled
                                "
                              />
                              <span class="dq-check-label">{{ action.name }}</span>
                            </label>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="modal-footer dq-modal-footer">
                <button
                  type="button"
                  class="btn btn-light dq-btn dq-btn-muted"
                  data-bs-dismiss="modal"
                  :disabled="store.loading"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  class="btn btn-primary dq-btn dq-btn-primary"
                  :disabled="store.loading"
                >
                  <span
                    v-if="store.loading"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  {{ isEdit ? "Save Changes" : "Save Client" }}
                  <i class="ti ti-arrow-right ms-2"></i>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Client (UPGRADED UI ONLY) -->
  <div class="modal fade dq-client-modal" id="delete_client" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-sm dq-del-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header dq-danger-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red">
              <i class="ti ti-trash"></i>
            </div>
            <div>
              <h5 class="modal-title dq-title mb-0">Confirm Delete</h5>
              <div class="dq-subtitle">This action cannot be undone.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-danger">
            <i class="ti ti-alert-octagon"></i>
            <div>
              <div class="dq-danger-title">Delete this client record?</div>
              <div class="dq-danger-sub">
                If linked to projects, tasks, or invoices, it may affect reports.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <button
            type="button"
            class="btn btn-light dq-btn dq-btn-muted"
            data-bs-dismiss="modal"
            :disabled="store.loading"
          >
            Cancel
          </button>

          <button
            @click="confirmDelete"
            type="button"
            class="btn btn-danger dq-btn dq-btn-danger"
            :disabled="store.loading"
          >
            <span
              v-if="store.loading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Delete
            <i class="ti ti-trash ms-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, watch, computed } from "vue";
import { useCrmClientStore } from "@/stores/crmClientStore";

const props = defineProps({
  client: { type: Object, default: null },
  nextCode: { type: String, default: "CLT-0001" },
});
const emit = defineEmits(["refresh"]);
const store = useCrmClientStore();

const formEl = ref(null);

const isEdit = computed(() => !!(props.client && props.client.id));
const displayClientCode = computed(() =>
  isEdit.value ? props.client.client_code : props.nextCode
);

// ----- Permissions -----
const permissionsList = reactive([
  {
    module: "Clients",
    permissionKey: "Client",
    enabled: false,
    actions: [
      { name: "Read", value: false },
    ],
  },
  {
    module: "Contacts",
    permissionKey: "Contact",
    enabled: false,
    actions: [
      { name: "Read", value: false },
      { name: "Write", value: false },
      { name: "Create", value: false },
      { name: "Delete", value: false },
      { name: "Import", value: false },
      { name: "Export", value: false },
    ],
  },
  {
    module: "Projects",
    permissionKey: "Project",
    enabled: false,
    actions: [
      { name: "Read", value: false },
      { name: "Write", value: false },
      { name: "Create", value: false },
      { name: "Delete", value: false },
      { name: "Import", value: false },
      { name: "Export", value: false },
    ],
  },
  {
    module: "Tasks",
    permissionKey: "Task",
    enabled: false,
    actions: [
      { name: "Read", value: false },
      { name: "Write", value: false },
      { name: "Create", value: false },
      { name: "Delete", value: false },
      { name: "Import", value: false },
      { name: "Export", value: false },
    ],
  },
  {
    module: "Invoices",
    permissionKey: "Invoice",
    enabled: false,
    actions: [
      { name: "Read", value: false },
      { name: "Export", value: false },
    ],
  },
  {
    module: "Payments",
    permissionKey: "Payment",
    enabled: false,
    actions: [
      { name: "Read", value: false },
      { name: "Export", value: false },
    ],
  },
]);

const requiredPermissionKeys = ["Client"];
const isRequiredModule = (perm) =>
  requiredPermissionKeys.includes(perm.permissionKey || perm.module);
const optionalPermissions = computed(() =>
  permissionsList.filter((p) => !isRequiredModule(p))
);
const hasEnabledOptionalModules = computed(() =>
  optionalPermissions.value.some((p) => p.enabled)
);
const allOptionalModulesEnabled = computed(
  () =>
    optionalPermissions.value.length > 0 &&
    optionalPermissions.value.every((p) => p.enabled)
);
const allEnabledActionsSelected = computed(() => {
  const enabled = optionalPermissions.value.filter((p) => p.enabled);
  if (!enabled.length) return false;
  return enabled.every((p) => p.actions.every((a) => a.value));
});

function syncSwitches() {
  return;
}

function defaultModuleActions(perm) {
  return perm.actions.map((a) => a.name);
}

function setModuleEnabled(perm, enabled, { preserveActions = false } = {}) {
  perm.enabled = enabled;
  if (enabled) {
    if (!preserveActions || !perm.actions.some((a) => a.value)) {
      const defaults = defaultModuleActions(perm);
      perm.actions.forEach((a) => {
        a.value = defaults.includes(a.name) && a.name === "Read";
      });
    }
  } else {
    perm.actions.forEach((a) => (a.value = false));
  }
}

function toggleModule(perm, enabled) {
  setModuleEnabled(perm, enabled);
  syncSwitches();
}

function toggleAction(perm, action, checked) {
  if (!perm.enabled) {
    setModuleEnabled(perm, true, { preserveActions: true });
  }
  action.value = checked;
  syncSwitches();
}

function setAllModules(enabled) {
  optionalPermissions.value.forEach((p) => setModuleEnabled(p, enabled));
  syncSwitches();
}

function setAllEnabledActions(enabled) {
  optionalPermissions.value
    .filter((p) => p.enabled)
    .forEach((p) => p.actions.forEach((a) => (a.value = enabled)));
  syncSwitches();
}

function getSelectedPermissions() {
  const out = [];
  permissionsList.forEach((p) => {
    if (!p.enabled) return;
    p.actions.forEach((a) => {
      if (a.value) out.push(`${p.permissionKey || p.module}.${a.name}`);
    });
  });
  if (canConfigurePortalPermissions.value && !out.includes("Client.Read")) {
    out.unshift("Client.Read");
  }
  return out;
}

// ----- Form -----
const baseForm = () => ({
  name: "",
  client_code: "",
  email: "",
  phone: "",
  website: "",
  address: "",
  status: "Active",
  logo: null,
  create_portal_user: false,
  permissions: [],
});
const form = reactive(baseForm());
const preview = ref("");

// track if client already has a portal user
const hadPortalUser = ref(false);
const canConfigurePortalPermissions = computed(
  () => hadPortalUser.value || form.create_portal_user
);

watch(
  () => [props.client, props.nextCode],
  ([c, clientId]) => {
    Object.assign(form, baseForm());
    permissionsList.forEach((p) => {
      p.enabled = false;
      p.actions.forEach((a) => (a.value = false));
    });
    preview.value = "";

    if (c) {
      form.name = c.name || "";
      form.client_code = c.client_code || "";
      form.email = c.email || "";
      form.phone = c.phone || "";
      form.website = c.website || "";
      form.address = c.address || "";
      form.status = c.status || "Active";
      preview.value = c.logo_url || "";

      // Prefill portal-user checkbox based on existing user presence
      hadPortalUser.value = !!(c?.user_id || c?.user);
      form.create_portal_user = hadPortalUser.value;

      // If BE returns user.permissions, map them into table (optional)
      if (Array.isArray(c?.user?.permissions)) {
        c.user.permissions.forEach((perm) => {
          const [mod, act] = String(perm).split(".");
          const m = permissionsList.find(
            (x) => x.module === mod || x.permissionKey === mod
          );
          if (m) {
            m.enabled = true;
            const a = m.actions.find((y) => y.name === act);
            if (a) a.value = true;
          }
        });
        syncSwitches();
      }
    } else {
      // Add-mode
      form.client_code = clientId || "";
      hadPortalUser.value = false; // 👈 clear the flag
      form.create_portal_user = false; // 👈 clear the checkbox
    }
  },
  { immediate: true }
);

watch(
  () => form.create_portal_user,
  (enabled) => {
    if (enabled || hadPortalUser.value) {
      const clientModule = permissionsList.find((p) => p.permissionKey === "Client");
      if (clientModule) {
        setModuleEnabled(clientModule, true, { preserveActions: true });
        clientModule.actions.forEach((a) => {
          a.value = a.name === "Read";
        });
      }
      syncSwitches();
      return;
    }
    permissionsList.forEach((p) => {
      p.enabled = false;
      p.actions.forEach((a) => (a.value = false));
    });
    syncSwitches();
  }
);

function onFileChange(e) {
  const f = e.target.files?.[0];
  if (!f) return;
  form.logo = f;
  const r = new FileReader();
  r.onload = (ev) => (preview.value = ev.target.result);
  r.readAsDataURL(f);
}
function clearFile() {
  form.logo = null;
  preview.value = "";
}

async function submit() {
  if (formEl.value && !formEl.value.checkValidity()) {
    formEl.value.reportValidity();
    return;
  }

  // gather permissions
  form.permissions = getSelectedPermissions();

  const fd = new FormData();
  // append non-boolean, non-file fields first
  Object.entries(form).forEach(([k, v]) => {
    if (k === "permissions") {
      v.forEach((p) => fd.append("permissions[]", p));
    } else if (k === "logo") {
      // skip here; handle below to only append if File
    } else if (k === "create_portal_user") {
      // handle below with conditional logic
    } else if (v !== null && v !== "") {
      fd.append(k, v);
    }
  });

  // append logo if it’s a File
  if (form.logo instanceof File) {
    fd.append("logo", form.logo);
  }

  // Only send create_portal_user when:
  // - creating a new client (always include), OR
  // - editing AND enabling for the first time (hadPortalUser=false & checkbox true)
  if (!isEdit.value) {
    fd.append("create_portal_user", form.create_portal_user ? 1 : 0);
  } else if (!hadPortalUser.value && form.create_portal_user) {
    fd.append("create_portal_user", 1);
  }

  try {
    if (isEdit.value) {
      fd.append("_method", "PUT");
      await store.updateClient(props.client.id, fd);
    } else {
      await store.createClient(fd);
    }
    emit("refresh");
    setTimeout(() => closeModal("add_client"), 100);
  } catch {
    /* errors toasted in store */
  }
}

function closeModal(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.classList.remove("show");
  el.style.display = "none";
  el.setAttribute("aria-hidden", "true");
  el.removeAttribute("aria-modal");
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

async function confirmDelete() {
  if (props.client && props.client.id) {
    await store.deleteClient(props.client.id);
    emit("refresh");
    closeModal("delete_client");
  }
}
</script>

<style scoped>
/* Wider + premium */
.dq-client-dialog {
  max-width: 1100px;
}
.dq-del-dialog {
  max-width: 520px;
}
@media (max-width: 992px) {
  .dq-client-dialog,
  .dq-del-dialog {
    max-width: 96%;
  }
}

/* Modal shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.14), #fff);
}
.dq-danger-head {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #fff);
}
.dq-modal-body {
  padding: 18px 22px;
  max-height: calc(100vh - 240px);
  overflow: auto; /* ✅ fixes scrolling inside modal */
}
.dq-modal-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

/* Header */
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-codewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.16);
  color: rgb(79, 70, 229);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* Client code chip */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 8px 12px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
  font-weight: 900;
}
.dq-chip-label {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-chip-value {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.9);
}

/* Tabs */
.dq-tabs {
  padding: 10px 22px 0;
  background: #fff;
}
.dq-nav .nav-link {
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dq-nav .nav-link.active {
  color: rgba(79, 70, 229, 1);
}
.dq-nav-link-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.dq-tab-lock {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  color: rgba(15, 23, 42, 0.6);
  font-size: 0.68rem;
  font-weight: 900;
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.55);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}
.dq-textarea {
  resize: vertical;
}

/* Logo card */
.dq-logo-card {
  display: flex;
  gap: 14px;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
  margin-bottom: 14px;
}
.dq-logo-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-avatar {
  width: 74px;
  height: 74px;
  border-radius: 999px;
  border: 2px dashed rgba(15, 23, 42, 0.18);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.dq-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.dq-logo-title {
  font-weight: 950;
}
.dq-logo-sub {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-logo-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

/* file input */
.dq-hidden-file {
  display: none;
}
.dq-btn-file {
  position: relative;
  overflow: hidden;
}
.dq-btn-muted {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}

/* tip */
.dq-tip {
  display: inline-flex;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.12);
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.75);
}

/* Portal switch row */
.dq-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 12px;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
}
.dq-switch-title {
  font-weight: 950;
}
.dq-switch-sub {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-note {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
  color: rgba(15, 23, 42, 0.75);
  font-size: 0.82rem;
}
.dq-note-success {
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.16);
  color: rgb(22, 101, 52);
}
.dq-note-muted {
  background: rgba(148, 163, 184, 0.08);
  border-color: rgba(148, 163, 184, 0.16);
  color: rgb(71, 85, 105);
}

/* Permissions header */
.dq-perm-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
  margin-bottom: 12px;
}
.dq-perm-title {
  font-weight: 950;
}
.dq-perm-sub {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-perm-controls {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}
.dq-perm-locked {
  margin-bottom: 12px;
  padding: 18px;
  border-radius: 18px;
  border: 1px dashed rgba(15, 23, 42, 0.14);
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.03),
    rgba(15, 23, 42, 0.015)
  );
  text-align: center;
}
.dq-perm-locked-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  background: rgba(15, 23, 42, 0.08);
  color: rgba(15, 23, 42, 0.75);
  font-size: 20px;
}
.dq-perm-locked-title {
  font-weight: 950;
  color: rgba(15, 23, 42, 0.95);
}
.dq-perm-locked-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Permissions table */
.dq-tablewrap {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-table td {
  vertical-align: middle;
  border-color: rgba(15, 23, 42, 0.06);
}
.dq-module-cell {
  min-width: 220px;
  background: rgba(15, 23, 42, 0.015);
}
.dq-module-name {
  font-weight: 950;
}
.dq-required-chip {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
  color: #4338ca;
  background: rgba(99, 102, 241, 0.12);
}
.dq-action-cell {
  min-width: 120px;
}
.dq-check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  cursor: pointer;
}
.dq-check-label {
  font-weight: 800;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.78);
}

/* Buttons */
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}

/* Delete warning */
.dq-danger {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 14px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.14);
  background: rgba(239, 68, 68, 0.08);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
  margin-top: 2px;
}
.dq-danger-title {
  font-weight: 950;
}
.dq-danger-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.7);
}
</style>
